<template lang="pug">
aside
  LabelledValue(label="Publication" :value="issue.publication.name")
  LabelledValue(label="Subsite" :value="issue.subsite.name" v-if="issue.subsite")
  LabelledValue(label="Slug" :value="issue.slug")
  LabelledValue(label="Publish date" :value="issue.publish_on")
  LabelledEditableValue(
    label="Notes" 
    v-model="issue.notes" 
    :multi-line="true" 
    @blur="save"
    :display-formatter="formatNotes"
  )

  AdobePreviewsSection.adobe-previews-in-sidebar
</template>

<script>
  import { mapStores, mapState } from 'pinia'
  import { useIssueStore } from '@/stores/issue'

  import LabelledValue from '@/components/shared/labelled_value.vue'
  import LabelledEditableValue from '@/components/shared/labelled_editable_value.vue'
  import AdobePreviewsSection from './adobe_previews_section.vue'

  export default {
    components: {
      LabelledValue,
      LabelledEditableValue,
      AdobePreviewsSection
    },

    computed: {
      ...mapStores(useIssueStore),
      ...mapState(useIssueStore, ['issue'])
    },

    methods: {
      save() {
        this.issueStore.update()
      },
      
      formatNotes(text) {
        return text ? text.replace(/\n/g, '<br />') : '';
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '@/src/variables';

  @media (max-width: $screen-md-max) {
    .adobe-previews-in-sidebar {
      display: none;
    }
  }
</style>

      },
      
      isCheckingVersion(previewId) {
        return this.checkingVersions[previewId] || false
      },

      getThumbnails(previewId) {
        return this.previewThumbnails[previewId] || []
      },

      async loadThumbnails(previewId) {
        try {
          const response = await httpClient.get(`/api/internal/adobe_previews/${previewId}/thumbnails`)
          this.$set(this.previewThumbnails, previewId, response.data.thumbnails || [])
        } catch (error) {
          console.error('Failed to load thumbnails:', error)
          this.$set(this.previewThumbnails, previewId, [])
        }
      },

      openUrlModal() {
        this.$refs.urlModal.show()
      },

      async handleUrlSave(url) {
        try {
          const response = await httpClient.post(`/api/internal/issues/${this.issue.id}/adobe_previews`, {
            adobe_preview: { preview_url: url }
          })
          
          await this.issueStore.fetch({ issueId: this.issue.id })
          this.$store.dispatch('messages/addNotice', 'Preview URL saved. Fetching preview...')
          
          // Start polling for the newly created preview
          const newPreview = response.data.adobe_preview
          if (newPreview) {
            this.$set(this.fetchingPreviews, newPreview.id, true)
            this.startPolling(newPreview.id)
          }
        } catch (error) {
          console.error('Failed to create preview:', error)
          this.$store.dispatch('messages/addError', error)
        }
      },

      async checkVersion(preview) {
        this.$set(this.checkingVersions, preview.id, true)
        try {
          await httpClient.post(`/api/internal/adobe_previews/${preview.id}/check_version`)
          
          // Refresh issue data to get updated preview
          await this.issueStore.fetch({ issueId: this.issue.id })
          
          const updatedPreview = this.adobePreviews.find(p => p.id === preview.id)
          
          // Check for metadata fetch errors
          if (updatedPreview?.fetch_status === 'metadata_error') {
            this.$store.dispatch('messages/addError', new Error(updatedPreview.fetch_message || 'Unable to check current version. Please try again in a few minutes.'))
            this.$set(this.checkingVersions, preview.id, false)
            return
          }
          
          // If update available, fetch it automatically
          if (updatedPreview?.update_available) {
            this.$store.dispatch('messages/addNotice', 'New version found! Fetching updated preview...')
            await this.fetchPreview(preview)
          } else {
            this.$store.dispatch('messages/addNotice', 'Preview is already up to date')
          }
          
        } catch (error) {
          console.error('Failed to check version:', error)
          this.$store.dispatch('messages/addError', error)
        } finally {
          this.$set(this.checkingVersions, preview.id, false)
        }
      },
      
      async fetchPreview(preview) {
        this.$set(this.fetchingPreviews, preview.id, true)
        try {
          await httpClient.post(`/api/internal/adobe_previews/${preview.id}/fetch`)
          this.startPolling(preview.id)
        } catch (error) {
          console.error('Failed to fetch preview:', error)
          this.$store.dispatch('messages/addError', error)
          this.$set(this.fetchingPreviews, preview.id, false)
        }
      },

      startPolling(previewId) {
        let attempts = 0
        const maxAttempts = 240 // 4 minutes total (240 × 1 seconds)

        this.pollIntervals[previewId] = setInterval(async () => {
          attempts++
          
          // Fetch fresh preview data
          try {
            const response = await httpClient.get(`/api/internal/adobe_previews/${previewId}`)
            const preview = response.data.adobe_preview
            
            // Update the preview in the issue's adobe_previews array
            const index = this.issue.adobe_previews.findIndex(p => p.id === previewId)
            if (index !== -1) {
              this.$set(this.issue.adobe_previews, index, preview)
            }
            
            // Check if fetch completed successfully
            const isComplete = preview.fetch_status === 'complete'
            const hasError = preview.fetch_status === 'error'
            
            if (isComplete) {
              this.stopPolling(previewId)
              this.$set(this.fetchingPreviews, previewId, false)
              this.$store.dispatch('messages/addNotice', `Preview fetch complete: ${preview.fetch_message || 'All thumbnails generated'}`)
              // Load thumbnails immediately
              await this.loadThumbnails(previewId)
              // Refresh files to show new thumbnails
              if (this.filesStore.resourceGlobalId) {
                this.filesStore.fetch()
              }
              return
            }
            
            if (hasError) {
              this.stopPolling(previewId)
              this.$set(this.fetchingPreviews, previewId, false)
              const errorMsg = preview.fetch_message || 'Unknown error'
              this.$store.dispatch('messages/addError', new Error(`Preview fetch failed: ${errorMsg}`))
              return
            }

            // Timeout after max attempts
            if (attempts >= maxAttempts) {
              this.stopPolling(previewId)
              this.$set(this.fetchingPreviews, previewId, false)
              this.$store.dispatch('messages/addError', new Error('Preview fetch is taking longer than expected'))
            }
          } catch (error) {
            console.error('Failed to poll preview status:', error)
            this.stopPolling(previewId)
            this.$set(this.fetchingPreviews, previewId, false)
          }
        }, 1000) // Poll every 1 second for maximum responsiveness
      },

      stopPolling(previewId) {
        if (this.pollIntervals[previewId]) {
          clearInterval(this.pollIntervals[previewId])
          delete this.pollIntervals[previewId]
        }
      },
      
      stopAllPolling() {
        Object.keys(this.pollIntervals).forEach(previewId => {
          this.stopPolling(previewId)
        })
      },

      async deletePreview(preview) {
        if (!confirm(`Are you sure you want to delete "${preview.preview_title || 'this preview'}"? This will delete the PDF and all generated thumbnails.`)) {
          return
        }

        try {
          await httpClient.delete(`/api/internal/adobe_previews/${preview.id}`)
          await this.issueStore.fetch({ issueId: this.issue.id })
          // Clear thumbnails from cache
          this.$delete(this.previewThumbnails, preview.id)
          // Refresh files to remove deleted thumbnails
          if (this.filesStore.resourceGlobalId) {
            this.filesStore.fetch()
          }
          this.$store.dispatch('messages/addNotice', 'Preview deleted successfully')
        } catch (error) {
          console.error('Failed to delete preview:', error)
          this.$store.dispatch('messages/addError', new Error('Failed to delete preview: ' + (error.response?.data?.error || error.message)))
        }
      }
    }
  }
</script>

<style scoped>
  .preview-section {
    margin-top: 40px;
    background-color: #FAFAFA;
    padding:10px;
    border-radius: 5px;
  }

  .preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
  }

  .preview-section h2 {
    display: inline-block;
    margin: 0;
  }

  .add-button {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .previews-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .no-previews {
    padding: 1rem 0;
    text-align: center;
  }

  .no-previews p {
    margin: 0;
    font-size: 14px;
  }
</style>
